import {
  Button,
  HexIcon,
  Icons,
  Panel,
  Placeholder,
  Stack,
} from '@cast/design-system';
import {
  dashesOrNum,
  formatCurrency,
  formatPercentage,
  notReachable,
  toNumOrUndefined,
} from '@cast/utils';

import { useAutoOptimization } from '../../../../_hooks/useAutoOptimization';
import { RebalancingPlanFlowState } from '../../../../_types';

import { Link } from '@cast/split-me/components/router';
import { getPrepareNewPlanLink } from '@cast/split-me/features/cluster/rebalance';
import { MetricsGrid } from '../MetricsGrid';

import { NormalizedRebalancingPlan } from '@cast/types';
import { RebalanceOptionLayout } from '../RebalanceOptionLayout';
import isNumber from 'lodash/isNumber';

const StyledPanel = ({ children }: { children: React.ReactNode }) => {
  return <Panel p={16}>{children}</Panel>;
};

const GeneratingRebalancingPlanPlaceholder = () => {
  return (
    <StyledPanel>
      <Placeholder
        icon={
          <HexIcon
            icon={<Icons.Sparkle />}
            size={40}
            variant="castPilotAssistant2"
          />
        }
        title="Generating rebalancing plan"
        body="This may take up to a couple of minutes for large clusters. "
      />
    </StyledPanel>
  );
};

const NoEligibleNodesPlaceholder = () => {
  return (
    <StyledPanel>
      <Stack alignItems="center" gap={4}>
        <Placeholder
          icon={
            <HexIcon icon={<Icons.Lightbulb />} size={40} variant="warning" />
          }
          title="No eligible nodes to rebalance"
        />
        <Button
          component={Link}
          to={getPrepareNewPlanLink()}
          target="_blank"
          variant="text"
          endIcon={<Icons.ArrowSquareOut size={12} />}
        >
          View details
        </Button>
      </Stack>
    </StyledPanel>
  );
};

const RebalancingNotNeededPlaceholder = () => {
  return (
    <StyledPanel>
      <Placeholder
        icon={<HexIcon size={40} variant="success" />}
        title="Cluster is optimal. Rebalancing isn’t needed immediately."
      />
    </StyledPanel>
  );
};

const RebalancingErrorPlaceholder = ({
  onTryAgain,
}: {
  onTryAgain: () => void;
}) => {
  return (
    <StyledPanel>
      <Stack alignItems="center" gap={8}>
        <Placeholder
          title="Failed to generate rebalancing plan"
          body="Node autoscaler will still work normally."
        />
        <Button
          variant="text"
          startIcon={<Icons.ArrowsCounterClockwise size={16} />}
          onClick={onTryAgain}
        >
          Try again
        </Button>
      </Stack>
    </StyledPanel>
  );
};

const RebalancingPlanDetails = ({
  normalizedRebalancingPlan,
}: {
  normalizedRebalancingPlan: NormalizedRebalancingPlan;
}) => {
  const currentCost = toNumOrUndefined(
    normalizedRebalancingPlan.configurations.blue?.clusterTotals?.priceMonthly
  );
  const estimatedCost = toNumOrUndefined(
    normalizedRebalancingPlan.configurations.green?.clusterTotals?.priceMonthly
  );

  const savingsMonthly = toNumOrUndefined(
    normalizedRebalancingPlan.configurations.diff?.priceMonthly
  );

  return (
    <MetricsGrid
      rows={[
        [
          {
            label: 'Savings',
            value: dashesOrNum({
              value:
                normalizedRebalancingPlan.configurations.diff
                  ?.savingsPercentage,
              mapNumber: (value) => formatPercentage(value),
            }),
            unit: isNumber(savingsMonthly)
              ? `(${formatCurrency(savingsMonthly, {
                  maximumFractionDigits: 0,
                })}/mo)`
              : undefined,
          },
          {
            label: 'Current cluster cost',
            value: isNumber(currentCost)
              ? formatCurrency(currentCost, { maximumFractionDigits: 0 })
              : '—',
            unit: '/mo',
          },
          {
            label: 'Est. rebalanced cost',
            value: isNumber(estimatedCost)
              ? formatCurrency(estimatedCost, { maximumFractionDigits: 0 })
              : '—',
            unit: '/mo',
          },
        ],
      ]}
      columnsPerRow={3}
    />
  );
};

const RebalancingPlanFlowSectionContent = ({
  rebalancingPlanFlowState,
  onRetryNodeRebalancePlan,
}: {
  rebalancingPlanFlowState: RebalancingPlanFlowState;
  onRetryNodeRebalancePlan: () => void;
}) => {
  switch (rebalancingPlanFlowState.type) {
    case 'generating-plan':
    case 'idle':
    case 'nodes-eligibility-check':
      return <GeneratingRebalancingPlanPlaceholder />;
    case 'no-eligible-nodes':
      return <NoEligibleNodesPlaceholder />;
    case 'plan-generated':
      if (rebalancingPlanFlowState.hasPositiveSavings) {
        return (
          <RebalancingPlanDetails
            normalizedRebalancingPlan={rebalancingPlanFlowState.plan}
          />
        );
      }
      return <RebalancingNotNeededPlaceholder />;
    case 'nodes-eligibility-check-error':
    case 'plan-generation-error':
      return (
        <RebalancingErrorPlaceholder onTryAgain={onRetryNodeRebalancePlan} />
      );
    default:
      return notReachable(rebalancingPlanFlowState);
  }
};

export const RebalancingPlanFlowSection = () => {
  const {
    rebalancingPlanFlowState,
    rebalanceImmediately,
    onToggleRebalanceOption,
    onRetryNodeRebalancePlan,
  } = useAutoOptimization();

  return (
    <RebalanceOptionLayout
      title="Rebalance immediately when setup is finished"
      {...(() => {
        switch (rebalancingPlanFlowState.type) {
          case 'generating-plan':
          case 'idle':
          case 'nodes-eligibility-check':
          case 'no-eligible-nodes':
          case 'nodes-eligibility-check-error':
          case 'plan-generation-error':
            return {
              checked: false,
              disabled: true,
            };
          case 'plan-generated':
            return rebalancingPlanFlowState.hasPositiveSavings
              ? {
                  checked: rebalanceImmediately,
                  disabled: false,
                }
              : {
                  checked: false,
                  disabled: true,
                };
          default:
            return notReachable(rebalancingPlanFlowState);
        }
      })()}
      onChange={() => onToggleRebalanceOption()}
    >
      <RebalancingPlanFlowSectionContent
        rebalancingPlanFlowState={rebalancingPlanFlowState}
        onRetryNodeRebalancePlan={onRetryNodeRebalancePlan}
      />
    </RebalanceOptionLayout>
  );
};
